import { useArtwork, useArtworkImage } from "@design-lab/entities/artwork";
import { type Side } from "@design-lab/shared";
import { trackAiStudioGeneratedDesignChosen } from "@design-lab/shared/lib/analytics";
import { useNotification } from "@design-lab/shared/ui/notification";
import { uploadAiImageToDesignLab } from "../services";
import { SAMPLE_IMAGE_URLS } from "../ui/components/AiImageGallery";
import { MENU_IDS, type AiDesignState } from "./state";
import { useArtworkSelection } from "./useArtworkSelection";

type UseAiImageSelectionOptions = {
  currentSide: Side;
  currentState: AiDesignState[Side];
  updateState: (updates: Partial<AiDesignState[Side]>) => void;
};

export function useAiImageSelection({
  currentSide,
  currentState,
  updateState
}: UseAiImageSelectionOptions) {
  const { setSelection, getActiveSelection, clearSelection } = useArtworkSelection();
  const { deleteArtwork, uploadArtwork } = useArtworkImage();
  const { getMachineBySide } = useArtwork();
  const { error: notifyError } = useNotification();

  const activeSelection = getActiveSelection(currentSide);
  const machine = getMachineBySide(currentSide);

  const handleImageSelect = async (imageId: number) => {
    if (
      activeSelection.activeSource === MENU_IDS.AI_DESIGN &&
      activeSelection.selectedId === imageId
    ) {
      return;
    }
    const selectedImage = currentState.images.find(img => img.id === imageId);
    if (!selectedImage) {
      notifyError({
        title: "Image Selection Failed",
        description: "Selected image not found. Please try again."
      });
      return;
    }

    trackAiStudioGeneratedDesignChosen(
      {
        url: selectedImage.imageSrc,
        style: selectedImage.style || {},
        provider: selectedImage.provider || ""
      },
      currentState.prompt
    );

    updateState({
      uploadingImageId: imageId,
      error: undefined
    });

    try {
      const existingArtworkId = machine.state.context.artworkId;
      if (existingArtworkId) {
        await deleteArtwork(currentSide, existingArtworkId.toString());
        clearSelection(currentSide);
      }

      const startTime = Date.now();
      updateState({ startTime });
      // Upload the AI-generated image using the existing upload flow
      // Only pass aiImageVersionId for actual generated images, not for sample images
      // Check if the selected image URL is from the predefined sample images
      const isSampleImage = SAMPLE_IMAGE_URLS.includes(selectedImage.imageSrc);
      const aiImageVersionId = isSampleImage ? undefined : String(imageId);
      setSelection(currentSide, MENU_IDS.AI_DESIGN, imageId);

      await uploadAiImageToDesignLab({
        imageUrl: selectedImage.imageSrc,
        imageId,
        uploadArtwork: file => {
          const params: Parameters<typeof uploadArtwork>[0] = {
            file,
            side: currentSide,
            source: "ai_generation"
          };
          if (aiImageVersionId) {
            params.aiImageVersionId = aiImageVersionId;
          }
          return uploadArtwork(params);
        }
      });
    } catch (error) {
      console.error("Failed to upload AI image:", error);
      window.Honeybadger?.notify("Failed to upload AI image", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          hook: "useAiImageSelection",
          side: currentSide
        }
      });
      clearSelection(currentSide);
      notifyError({
        title: "Upload Failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to upload AI-generated image. Please try again."
      });
    } finally {
      updateState({ uploadingImageId: undefined, startTime: undefined });
    }
  };

  return {
    handleImageSelect,
    activeSelection
  };
}
